import { zodResolver } from "@hookform/resolvers/zod";
import { Building2 } from "lucide-react";
import { memo, useEffect } from "react";
import { useForm } from "react-hook-form";

import { CompanyData, companySchema } from "../model";

import { CompanyDto } from "@/common/api";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Textarea,
} from "@/common/components";

interface ProfileComponyFormProps {
  isPending: boolean;
  data?: CompanyDto;
  onSubmit: (data: CompanyData) => void;
  onCancel: () => void;
}

export const CompanyForm = memo<ProfileComponyFormProps>(
  ({ isPending, data, onSubmit, onCancel }) => {
    const form = useForm<CompanyData>({
      resolver: zodResolver(companySchema),
      mode: "onChange",
      defaultValues: {
        id: data?.id,
        companyName: "",
        inn: "",
        kpp: "",
        ogrn: "",
        contactNumber: "",
        companyEmail: "",
        addressLegal: "",
        addressActual: "",
      },
    });

    const isReadOnly = true;

    useEffect(() => {
      if (data) {
        form.reset({ ...data });
      }
    }, [data, form]);

    return (
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 mt-6" noValidate>
          {/* Основная информация о компании */}
          <fieldset disabled={isReadOnly}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 md:text-2xl text-lg md:font-semibold font-bold md:leading-7 leading-6">
                  <Building2 className="md:h-6 h-5 md:w-6 w-5" />
                  Основная информация о компании
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 md:gap-y-6 gap-y-4">
                  <input type="hidden" {...form.register("id")} />
                  <FormField
                    control={form.control}
                    name="companyName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Наименование компании</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Введите наименование компании" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="inn"
                    render={({ field, fieldState: { invalid } }) => (
                      <FormItem>
                        <FormLabel>ИНН</FormLabel>
                        <FormControl>
                          <Input placeholder="Введите ИНН" {...field} />
                        </FormControl>
                        {!invalid && <FormDescription>10 или 12 цифр</FormDescription>}
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="kpp"
                    render={({ field, fieldState: { invalid } }) => (
                      <FormItem>
                        <FormLabel>КПП</FormLabel>
                        <FormControl>
                          <Input placeholder="Введите КПП" {...field} />
                        </FormControl>
                        {!invalid && <FormDescription>9 цифр</FormDescription>}

                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="ogrn"
                    render={({ field, fieldState: { invalid } }) => (
                      <FormItem>
                        <FormLabel>ОГРН</FormLabel>
                        <FormControl>
                          <Input placeholder="Введите ОГРН" {...field} />
                        </FormControl>
                        {!invalid && <FormDescription>13 или 15 цифр</FormDescription>}
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="companyEmail"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Электронная почта компании</FormLabel>
                        <FormControl>
                          <Input placeholder="Введите электронную почту" type="email" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="contactNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Номер телефона компании</FormLabel>
                        <FormControl>
                          <Input placeholder="+7xxxxxxxxxx" {...field} value={field.value || ""} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="addressLegal"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Юридический адрес</FormLabel>
                        <FormControl>
                          <Textarea placeholder="Введите юридический адрес" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="addressActual"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Фактический адрес</FormLabel>
                        <FormControl>
                          <Textarea placeholder="Введите фактический адрес" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>
          </fieldset>

          {/* Кнопки действий */}
          <div className="flex md:flex-row flex-col justify-end gap-3">
            {/* <Button type="button" variant="outline" onClick={onCancel}>
            Отменить
          </Button> */}

            {/* <Button
            type="submit"
            variant="secondary"
            disabled={isPending}
            className="flex items-center md:gap-2"
          >
            {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            <Send className="md:block hidden h-4 w-4" />
            Отправить изменения на проверку
          </Button> */}
          </div>
        </form>
      </Form>
    );
  },
);
